import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  afterRenderEffect,
  computed,
  inject,
  input,
  model,
  viewChildren,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';

export interface PageTab {
  id: string;
  label: string;
  /** Trusted inline SVG markup shown before the label. */
  icon?: string;
  /** Turns the bar into navigation links; the router decides the current tab. */
  route?: string;
  /** With `route`: current only on that exact URL, not on the routes below it. */
  exact?: boolean;
  disabled?: boolean;
  /** Id of the panel this tab shows, for `aria-controls`. */
  controls?: string;
}

/**
 * Underline tabs under a page header.
 *
 * Without routes it is an ARIA tablist: one Tab stop (the active tab), arrow
 * keys and Home/End move and select in one step, skipping disabled tabs.
 * With routes it is a `<nav>` of links marked `aria-current="page"`.
 * On narrow screens the bar scrolls sideways; the current tab and any tab that
 * takes focus are scrolled fully into view, ring included.
 */
@Component({
  selector: 'app-page-tabs-cabinet',
  templateUrl: './page-tabs.component.html',
  styleUrl: './page-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [RouterLink, RouterLinkActive, SafeHtmlPipe],
})
export class PageTabsCabinetComponent {
  private static nextId = 0;

  readonly tabs = input.required<readonly PageTab[]>();
  readonly active = model<string | null>(null);
  readonly ariaLabel = input<string>('');

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly tabButtons = viewChildren<ElementRef<HTMLButtonElement>>('tabButton');
  private readonly instanceId = `page-tabs-${PageTabsCabinetComponent.nextId++}`;

  readonly routed = computed(() => this.tabs().some((t) => !!t.route));

  /** The tab holding the Tab stop: the active one if usable, else the first enabled one. */
  readonly tabStopId = computed<string | null>(() => {
    const tabs = this.tabs();
    if (!tabs.length) return null;
    const current = tabs.find((t) => t.id === this.active() && !t.disabled);
    return (current ?? tabs.find((t) => !t.disabled) ?? tabs[0]).id;
  });

  constructor() {
    // Keeps the current tab visible in the scrolling bar (after load and after a change).
    afterRenderEffect(() => {
      this.active();
      this.tabs();
      const root = this.host.nativeElement.shadowRoot ?? this.host.nativeElement;
      const current = root.querySelector<HTMLElement>('.tab--active, [aria-current="page"]');
      if (current) this.revealTab(current);
    });
  }

  /** A tab took focus (Tab key, arrow keys, a click): show all of it. */
  onFocusIn(event: FocusEvent): void {
    const tab = (event.target as HTMLElement | null)?.closest?.<HTMLElement>('.tab');
    if (tab) this.revealTab(tab);
  }

  /** Scrolls the bar sideways only, so the page itself never jumps. */
  private revealTab(tab: HTMLElement): void {
    const bar = tab.parentElement;
    if (!bar || bar.scrollWidth <= bar.clientWidth) return;
    const gap = 16;
    const left = tab.offsetLeft;
    const right = left + tab.offsetWidth;
    if (left - gap < bar.scrollLeft) bar.scrollLeft = Math.max(0, left - gap);
    else if (right + gap > bar.scrollLeft + bar.clientWidth) bar.scrollLeft = right + gap - bar.clientWidth;
  }

  /** Element id of a tab, so a panel can point back with `aria-labelledby`. */
  tabElementId(id: string): string {
    return `${this.instanceId}-${id}`;
  }

  select(tab: PageTab): void {
    if (tab.disabled) return;
    this.active.set(tab.id);
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const tabs = this.tabs();
    if (tabs.every((t) => t.disabled)) return;

    let step: number;
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        step = 1;
        next = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        step = -1;
        next = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        step = 1;
        next = 0;
        break;
      case 'End':
        step = -1;
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    while (tabs[next].disabled) {
      next = (next + step + tabs.length) % tabs.length;
    }
    event.preventDefault();
    this.select(tabs[next]);
    this.tabButtons()[next]?.nativeElement.focus();
  }
}
